import { useState } from 'react';
import type { ReactElement } from 'react';
import { File, FileAudio, FileText, Image as ImageIcon } from 'lucide-react';
import type { PreviewDescriptor } from '@xdocs/schemas';
import { cn } from '@xdocs/ui';
import styles from './AssetPreviewThumb.module.css';

export interface AssetPreviewThumbProps {
  preview: PreviewDescriptor | null;
  alt: string;
}

const KIND_ICON: Record<PreviewDescriptor['kind'], typeof File> = {
  image: ImageIcon,
  'book-cover': ImageIcon,
  pdf: FileText,
  docx: FileText,
  text: FileText,
  audio: FileAudio,
  custom: File,
};

/**
 * Small F60 inspect thumbnail. `@xdocs/ui` doesn't export a shared
 * `<Preview>` renderer yet (F32 hasn't shipped a component under that name
 * in this package as of this task — see returned open-issues list), so this
 * is a minimal graceful-degrade box: `posterUrl` image with an `onError`
 * fallback to a kind icon (web-performance.md: "every `<img>` has an
 * `onerror` fallback to a styled illustration, never a broken browser
 * icon"), reserved square dimensions so a failed load doesn't shift layout.
 */
export function AssetPreviewThumb({ preview, alt }: AssetPreviewThumbProps): ReactElement {
  const [errored, setErrored] = useState(false);
  const Icon = preview ? KIND_ICON[preview.kind] : File;

  if (!preview?.posterUrl || errored) {
    return (
      <div className={cn(styles['placeholder'])} aria-hidden={alt === ''}>
        <Icon size={20} aria-hidden="true" />
      </div>
    );
  }

  return (
    <img
      src={preview.posterUrl}
      alt={alt}
      width={40}
      height={40}
      className={cn(styles['thumb'])}
      onError={() => setErrored(true)}
    />
  );
}
